import apiClient from './client';

export interface User {
  id: string;
  email: string | null;
  username: string;
  firstName: string | null;
  lastName: string | null;
  role: 'user' | 'admin';
  is_anonymous: boolean;
  isSubscribed: boolean;
  isBlocked: boolean;
  isDeleted: boolean;
  deletedAt: Date | null;
  subscribedEnd: Date | null;
  lastLogin: Date | null;
  created_at: Date;
  updated_at: Date;
  mock_exam_attempts_used: number;
  wrong_exam_attempts_used: number;
  marathon_attempts_used: number;
  goal: 'first_exam' | 'retake' | 'repeat' | null;
  deadline: '3_days' | '1_week' | '1_month' | null;
  deviceId: string | null;
  googleId: string | null;
  appleId: string | null;
  phone: string | null;
  telegramId: string | null;
  telegramUsername: string | null;
  mergedIntoUserId: string | null;
  mergedAt: Date | null;
  gamerTag: string | null;
}

// ─── Вкладки карточки пользователя ────────────────────────────────────────────

export interface UserPaymentRow {
  id: string;
  provider: 'click' | 'payme' | 'paylov';
  status: 'initiated' | 'success' | 'failed' | 'expired';
  amount: number;
  currency: string;
  otpSentPhone: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface UserOrderRow {
  id: string;
  status: 'pending' | 'paid' | 'cancelled' | 'refunded';
  planTitle: string | null;
  planCode: string | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  currency: string;
  promoCode: string | null;
  createdAt: string;
  paidAt: string | null;
  payments: UserPaymentRow[];
}

export interface UserPaymentsInfo {
  totals: { ordersCount: number; paidCount: number; paidSum: number };
  pricingVariant: { name: string | null; platform: string | null; assignedAt: string } | null;
  orders: UserOrderRow[];
  subscriptions: Array<{
    id: string;
    orderId: string;
    status: 'active' | 'expired' | 'cancelled';
    startsAt: string;
    endsAt: string;
    createdAt: string;
  }>;
}

export interface UserSessionRow {
  id: string;
  deviceId: string;
  deviceName: string | null;
  deviceModel: string | null;
  platform: 'ios' | 'android' | 'web';
  osVersion: string | null;
  appVersion: string | null;
  ipAddress: string | null;
  country: string | null;
  city: string | null;
  isActive: boolean;
  lastActiveAt: string;
  createdAt: string;
}

export interface UserDevicesInfo {
  sessions: UserSessionRow[];
  pushDevices: Array<{
    id: string;
    deviceType: string | null;
    appVersion: string | null;
    isActive: boolean;
    hasPushToken: boolean;
    failedAttempts: number;
    lastUsedAt: string | null;
    createdAt: string;
  }>;
}

export interface UserSecurityEvent {
  id: string;
  eventType: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  ipAddress: string | null;
  country: string | null;
  deviceId: string | null;
  userAgent: string | null;
  loginMethod: string | null;
  failureReason: string | null;
  message: string | null;
  createdAt: string;
}

export interface UserEngagementInfo {
  games: {
    dailyStreak: number;
    dailyBestStreak: number;
    dailyLastAnswered: string | null;
    timeAttackBest: number;
    survivalBest: number;
    eloRating: number;
    duelWins: number;
    duelLosses: number;
    duelDraws: number;
  } | null;
  ai: { days: number; requests: number; tokens: number; activeDays: number; lastUsedAt: string | null };
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Полный UUID или его начало (от 4 символов) — как id показан в таблице. */
  id?: string;
  role?: 'user' | 'admin';
  isSubscribed?: string;
  /** 'false' — только активные (по умолчанию), 'true' — только удалённые, 'all' — все */
  isDeleted?: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UpdateUserDto {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  role?: 'user' | 'admin';
  isSubscribed?: boolean;
  /** Дата окончания подписки (ISO). Обязательна при выдаче премиума. */
  subscribedEnd?: string | null;
  isBlocked?: boolean;
}

export interface UserLimits {
  mock_exam_attempts_used: number;
  wrong_exam_attempts_used: number;
  marathon_attempts_used: number;
  mock_exam_attempts_remaining: number;
  wrong_exam_attempts_remaining: number;
  marathon_attempts_remaining: number;
  isPremium: boolean;
}

export interface UserDetails extends User {
  limits: UserLimits;
}

export interface UsersStats {
  total: number;
  activeUsers: {
    day: number;
    week: number;
    month: number;
  };
}

export interface DailyActiveUsersData {
  day: string;
  registrations: number;
  successfulPayments: number;
}

export interface ActiveUsersComparison {
  name: string;
  current: number;
  previous: number;
}

export interface DashboardStats {
  totalUsers: number;
  subscribedUsers: number;
  totalRevenue: number;
  dailyActiveUsers: DailyActiveUsersData[];
  activeUsersByPeriod: ActiveUsersComparison[];
}

export const usersApi = {
  getUsers: async (params: GetUsersParams = {}): Promise<UsersListResponse> => {
    const response = await apiClient.get<UsersListResponse>('/users', { params });
    return response.data;
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  updateUser: async (id: string, dto: UpdateUserDto): Promise<User> => {
    const response = await apiClient.patch<User>(`/users/${id}`, dto);
    return response.data;
  },

  deleteUser: async (id: string): Promise<User> => {
    const response = await apiClient.delete<User>(`/users/${id}`);
    return response.data;
  },

  restoreUser: async (id: string): Promise<User> => {
    const response = await apiClient.post<User>(`/users/${id}/restore`);
    return response.data;
  },

  getUserDetails: async (id: string): Promise<UserDetails> => {
    const response = await apiClient.get<UserDetails>(`/users/${id}/details`);
    return response.data;
  },

  getUserPayments: async (id: string): Promise<UserPaymentsInfo> => {
    const response = await apiClient.get<UserPaymentsInfo>(`/users/${id}/payments`);
    return response.data;
  },

  getUserDevices: async (id: string): Promise<UserDevicesInfo> => {
    const response = await apiClient.get<UserDevicesInfo>(`/users/${id}/devices`);
    return response.data;
  },

  getUserSecurityLog: async (id: string): Promise<{ items: UserSecurityEvent[] }> => {
    const response = await apiClient.get<{ items: UserSecurityEvent[] }>(`/users/${id}/security-log`);
    return response.data;
  },

  getUserEngagement: async (id: string): Promise<UserEngagementInfo> => {
    const response = await apiClient.get<UserEngagementInfo>(`/users/${id}/engagement`);
    return response.data;
  },

  getUsersStats: async (): Promise<UsersStats> => {
    const response = await apiClient.get<UsersStats>('/users/stats');
    return response.data;
  },

  getDashboardStats: async (): Promise<DashboardStats> => {
    const response = await apiClient.get<DashboardStats>('/users/dashboard-stats');
    return response.data;
  },
};

